import { useState } from "react";
import { supabase } from "@/lib/supabase";import { toast } from "sonner";
import { ArrowRight, Loader2, Download } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { RESUME_URL } from "@/data/content";

const EMPTY = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
    const content = useContent();
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

const submit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
        const { error } = await supabase.from("messages").insert([
            {
                name: form.name.trim(),
                email: form.email.trim(),
                subject: form.subject.trim(),
                message: form.message.trim(),
            },
        ]);

        if (error) throw error;

        toast.success("Message received — thank you for reaching out.");
        setForm(EMPTY);
    } catch (err) {
        console.error("Contact form submission failed:", err.message);
        toast.error("Could not save your message. Please try again.");
    } finally {
        setSending(false);
    }
};

    return (
        <>
            <PageHeader
                crumb="HOME / CONTACT"
                eyebrow="06 — SAY HELLO"
                lines={["GET IN TOUCH"]}
                sub="Open to opportunities, collaborations and interesting conversations."
            />
            <section className="px-5 lg:px-12 xl:px-16 py-14 lg:py-20 pb-24 grid gap-14 lg:grid-cols-[1fr_1.2fr]">
                <Reveal>
                    <ul className="space-y-7" data-testid="contact-details">
                        <li>
                            <p className="mono text-[10px] tracking-[0.25em] text-muted2">
                                EMAIL
                            </p>
                            <a
                                href={`mailto:${content.contact.email}`}
                                data-testid="contact-detail-email"
                                className="mt-1 inline-block text-lg transition-colors hover:text-muted2 break-all"
                            >
                                {content.contact.email}
                            </a>
                        </li>
                        <li>
                            <p className="mono text-[10px] tracking-[0.25em] text-muted2">
                                LINKEDIN
                            </p>
                            <a
                                href={content.contact.linkedin_url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-detail-linkedin"
                                className="mt-1 inline-flex items-center gap-2 text-lg transition-colors hover:text-muted2 break-all"
                            >
                                {content.contact.linkedin_handle}
                                <ArrowRight className="h-4 w-4 shrink-0" />
                            </a>
                        </li>
                        <li>
                            <p className="mono text-[10px] tracking-[0.25em] text-muted2">
                                INSTAGRAM
                            </p>
                            <a
                                href={content.contact.instagram_url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-detail-instagram"
                                className="mt-1 inline-block text-lg transition-colors hover:text-muted2"
                            >
                                {content.contact.instagram_handle}
                            </a>
                        </li>
                        <li>
                            <p className="mono text-[10px] tracking-[0.25em] text-muted2">
                                BASED IN
                            </p>
                            <p
                                className="mt-1 text-lg"
                                data-testid="contact-detail-location"
                            >
                                {content.contact.location}
                            </p>
                        </li>
                    </ul>
                    <a
                        href={RESUME_URL}
                        data-testid="contact-download-resume"
                        className="group inline-flex items-center gap-2 mt-10 font-semibold underline underline-offset-8 decoration-sage decoration-2 transition-colors hover:text-muted2"
                    >
                        Download Resume
                        <Download className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
                    </a>
                </Reveal>

                <Reveal delay={0.12}>
                    <form
                        onSubmit={submit}
                        className="border border-line bg-paper2 p-8 lg:p-10"
                        data-testid="contact-form"
                    >
                        {[
                            ["name", "NAME", "text", true],
                            ["email", "EMAIL", "email", true],
                            ["subject", "SUBJECT (OPTIONAL)", "text", false],
                        ].map(([k, l, type, req]) => (
                            <label key={k} className="block mb-8">
                                <span className="mono text-[10px] tracking-[0.25em] text-muted2">
                                    {l}
                                </span>
                                <input
                                    type={type}
                                    required={req}
                                    value={form[k]}
                                    onChange={set(k)}
                                    data-testid={`contact-${k}-input`}
                                    className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-base transition-colors"
                                />
                            </label>
                        ))}
                        <label className="block mb-10">
                            <span className="mono text-[10px] tracking-[0.25em] text-muted2">
                                MESSAGE
                            </span>
                            <textarea
                                required
                                rows={5}
                                value={form.message}
                                onChange={set("message")}
                                data-testid="contact-message-input"
                                className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-base resize-none transition-colors"
                            />
                        </label>
                        <button
                            type="submit"
                            disabled={sending}
                            data-testid="contact-submit-button"
                            className="group inline-flex items-center gap-3 bg-ink text-paper px-8 py-4 mono text-[11px] tracking-[0.25em] uppercase transition-colors hover:bg-sage hover:text-ink disabled:opacity-60"
                        >
                            {sending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    SEND MESSAGE
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1.5" />
                                </>
                            )}
                        </button>
                    </form>
                </Reveal>
            </section>
        </>
    );
}
